import { type FormEvent, type ReactElement, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EFFECT_CLASS, Z_INDEX } from '@vello/tokens';
import { ShieldCheckIcon } from './icons';
import { OtpInput } from './otp-input';
import { useEscape, useLayer, useLayerStack, useModalFocus } from './overlays';
import { Button, Notice } from './primitives';

/**
 * The second sign-in step of every Vello console — the Admin's A01b "separate MFA step" as ONE component (V-LIB-SHELL,
 * D509; audit consoles F02 + admin #3). Five hand-rolled copies (admin, pdv, merchant, fleet, agent) left Tab walking
 * out into the credentials form behind the scrim, never gave focus back, listened for Escape on `document` without
 * regard for layering and discarded a typed code on a stray scrim click.
 *
 * It is a modal layer of the shared choreography (`useLayer` + `useLayerStack` + `useEscape` + `useModalFocus`):
 *   - focus moves to the code field when it opens, Tab / Shift+Tab stay inside, and focus goes back to the control that
 *     was focused when it opened (the credentials form's field or button) when it closes;
 *   - Escape cancels — only while this is the top layer (an `onCancel` is also the "Back" button);
 *   - a click on the scrim does NOTHING: a typed code is never discarded by a stray click;
 *   - it is portalled to `document.body`, so inside the navy sign-in panel (whose roles are re-pointed at their
 *     on-inverse counterparts) it keeps the light palette.
 * The scrim is the theme's `vello-scrim` class (its blur is the `--scrim-blur` token, not a literal).
 *
 * The code lives in the dialog (it resets every time the dialog opens); `onSubmit(code)` gets the normalised digits when
 * the form is submitted with a full code — Enter in the field submits too. `error` is shown in a `role="alert"` notice
 * and marks the field invalid. `resend` is a slot under the field for the app's own "send a new code" control. Every
 * label is the caller's (translated) text.
 */
export interface MfaDialogProps {
  /** Mounts the layer (default `true`; pass the step flag to get the exit choreography instead of unmounting). */
  readonly open?: boolean;
  readonly title: string;
  readonly description?: string;
  /** Number of digits of the code (default 6). */
  readonly codeLength?: number;
  /** The code field's accessible name ("Verification code"). */
  readonly codeLabel: string;
  readonly submitLabel: string;
  /** The submit button's label while `busy` (defaults to `submitLabel`). */
  readonly busyLabel?: string;
  /** The secondary "back" action's label ("Back to sign-in"). */
  readonly cancelLabel: string;
  readonly onSubmit: (code: string) => void;
  /** Escape and the cancel button. Never called by a scrim click. */
  readonly onCancel: () => void;
  readonly busy?: boolean;
  /** A refused code ("That code is not valid"); shown as an alert under the field. */
  readonly error?: ReactNode;
  /** Slot under the field for the app's resend control (a ghost `Button`). */
  readonly resend?: ReactNode;
}

export function MfaDialog({ open = true, title, description, codeLength = 6, codeLabel, submitLabel, busyLabel, cancelLabel, onSubmit, onCancel, busy = false, error, resend }: MfaDialogProps): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  const ref = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState('');
  const titleId = useId();
  const bodyId = useId();
  const errorId = useId();
  const isTop = useLayerStack(open);
  useEscape(open, onCancel, isTop);
  useModalFocus(ref, open, mounted, isTop);
  useEffect(() => {
    if (open) setCode('');
  }, [open]);
  if (!mounted) return null;

  const ready = code.length === codeLength;
  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (busy || !ready) return;
    onSubmit(code);
  };
  const hasError = error !== undefined && error !== null && error !== false && error !== '';

  return createPortal(
    <>
      {/* Deliberately no onClick: a click on the scrim never discards the typed code. */}
      <div className={`vello-scrim ${EFFECT_CLASS.scrim} ${cls}`} style={{ zIndex: Z_INDEX.modal + 2 }} aria-hidden="true" data-testid="mfa-scrim" />
      <div className="vello-dialog-host" style={{ zIndex: Z_INDEX.modal + 3 }}>
        <div
          ref={ref}
          tabIndex={-1}
          className={`vello-dialog vello-mfa ${EFFECT_CLASS.dialog} ${cls}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description !== undefined ? bodyId : undefined}
          data-vello-layer={open ? 'open' : 'closing'}
        >
          <span className="vello-dialog__icon vello-mfa__icon" aria-hidden="true">
            <ShieldCheckIcon size={24} />
          </span>
          <h2 id={titleId} className="vello-dialog__title">
            {title}
          </h2>
          {description !== undefined && (
            <p id={bodyId} className="vello-dialog__body">
              {description}
            </p>
          )}
          <form className="vello-mfa__form" onSubmit={submit} noValidate>
            <OtpInput value={code} onChange={setCode} length={codeLength} label={codeLabel} invalid={hasError} describedBy={hasError ? errorId : undefined} autoFocus />
            {hasError && (
              <div id={errorId}>
                <Notice tone="danger">{error}</Notice>
              </div>
            )}
            {resend !== undefined && <div className="vello-mfa__resend">{resend}</div>}
            <div className="vello-mfa__actions">
              <Button type="submit" variant="primary" size="lg" block disabled={busy || !ready}>
                {busy ? (busyLabel ?? submitLabel) : submitLabel}
              </Button>
              <Button variant="ghost" block onClick={onCancel}>
                {cancelLabel}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
